import { useEffect, useState } from 'react';
import { alertsApi, promosApi } from '@/lib/api';
import { Alert, Promo, AlertSeverity } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { formatTime, formatCurrency } from '@/lib/format';
import {
  Bell,
  AlertTriangle,
  Info,
  XCircle,
  CheckCircle,
  Tag,
  Calendar,
  TrendingUp,
} from 'lucide-react';

type Tab = 'alerts' | 'promos';

export default function AlertsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('alerts');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [alertStats, setAlertStats] = useState({ total: 0, critical: 0, warning: 0, info: 0 });
  const [promoStats, setPromoStats] = useState({ total: 0, active: 0, upcoming: 0, expired: 0 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
    loadPromos();
  }, []);

  const loadAlerts = async () => {
    try {
      setIsLoading(true);
      const [alertsData, stats] = await Promise.all([
        alertsApi.getAlerts(),
        alertsApi.getStats(),
      ]);
      setAlerts(alertsData);
      setAlertStats(stats);
    } catch (error) {
      logger.error('Failed to load alerts', error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPromos = async () => {
    try {
      const [promosData, stats] = await Promise.all([
        promosApi.getPromos(),
        promosApi.getStats(),
      ]);
      setPromos(promosData);
      setPromoStats(stats);
    } catch (error) {
      logger.error('Failed to load promos', error as Error);
    }
  };

  const handleMarkAsRead = async (alertId: string) => {
    try {
      await alertsApi.markAsRead(alertId);
      // Update local state (move to metadata)
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      setAlertStats((prev) => ({
        ...prev,
        total: prev.total - 1,
      }));
      logger.info('Alert marked as read', { alertId });
    } catch (error) {
      logger.error('Failed to mark alert as read', error as Error);
    }
  };

  const getSeverityIcon = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getPromoKindLabel = (kind: string) => {
    switch (kind) {
      case 'percentage':
        return 'Percentage Off';
      case 'fixed_amount':
        return 'Fixed Discount';
      case 'free_delivery':
        return 'Free Delivery';
      default:
        return kind;
    }
  };

  const getPromoValue = (kind: string, value: string) => {
    const numValue = parseFloat(value);
    switch (kind) {
      case 'percentage':
        return `${numValue}%`;
      case 'fixed_amount':
        return formatCurrency(numValue);
      case 'free_delivery':
        return 'Free';
      default:
        return value;
    }
  };

  const isPromoActive = (promo: Promo) => {
    const now = new Date();
    const starts = new Date(promo.starts_at);
    const ends = new Date(promo.ends_at);
    return promo.is_active && now >= starts && now <= ends;
  };

  const isPromoUpcoming = (promo: Promo) => {
    const now = new Date();
    const starts = new Date(promo.starts_at);
    return promo.is_active && now < starts;
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Alerts & Promos</h1>
          <p className="text-muted-foreground">Stay updated with system notifications and promotions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Alert Stats */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Alerts</p>
              <Bell className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">{alertStats.total}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Critical</p>
              <XCircle className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">{alertStats.critical}</p>
          </div>

          {/* Promo Stats */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Active Promos</p>
              <Tag className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-600">{promoStats.active}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Upcoming</p>
              <Calendar className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-600">{promoStats.upcoming}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('alerts')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'alerts'
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Alerts ({alertStats.total})
            {activeTab === 'alerts' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'promos'
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Promos ({promoStats.active})
            {activeTab === 'promos' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <>
            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <div className="space-y-4">
                {alerts.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">All Clear!</h3>
                    <p className="text-muted-foreground">No active alerts at the moment</p>
                  </div>
                ) : (
                  alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`border rounded-xl p-6 transition-colors ${getSeverityColor(
                        alert.severity
                      )}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 mt-1">{getSeverityIcon(alert.severity)}</div>

                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-foreground mb-1">
                                {alert.alert_type}
                              </h3>
                              <p className="text-sm text-muted-foreground">{alert.message}</p>
                            </div>
                            <button
                              onClick={() => handleMarkAsRead(alert.id)}
                              className="ml-4 text-xs px-3 py-1 bg-white border border-border rounded-lg hover:bg-muted transition-colors"
                            >
                              Mark as Read
                            </button>
                          </div>

                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                            <span>{formatTime(alert.created_at)}</span>
                            <span className="uppercase font-medium">{alert.severity}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Promos Tab */}
            {activeTab === 'promos' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {promos.length === 0 ? (
                  <div className="col-span-full bg-card border border-border rounded-xl p-12 text-center">
                    <Tag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Promos Available</h3>
                    <p className="text-muted-foreground">Check back later for new promotions</p>
                  </div>
                ) : (
                  promos.map((promo) => {
                    const active = isPromoActive(promo);
                    const upcoming = isPromoUpcoming(promo);

                    return (
                      <div
                        key={promo.id}
                        className={`border rounded-xl p-6 ${
                          active
                            ? 'bg-green-50 border-green-200'
                            : upcoming
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {/* Badge */}
                        <div className="flex items-center justify-between mb-4">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                              active
                                ? 'bg-green-100 text-green-700'
                                : upcoming
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {active ? (
                              <>
                                <TrendingUp className="w-3 h-3" />
                                Active Now
                              </>
                            ) : upcoming ? (
                              <>
                                <Calendar className="w-3 h-3" />
                                Upcoming
                              </>
                            ) : (
                              'Expired'
                            )}
                          </span>

                          <Tag className="w-5 h-5 text-muted-foreground" />
                        </div>

                        {/* Code & Value */}
                        <div className="mb-4">
                          <h3 className="text-2xl font-bold text-foreground mb-1">{promo.code}</h3>
                          <p className="text-sm text-muted-foreground">
                            {getPromoKindLabel(promo.kind)}
                          </p>
                        </div>

                        {/* Value */}
                        <div className="bg-white rounded-lg p-4 mb-4">
                          <p className="text-3xl font-bold text-primary">
                            {getPromoValue(promo.kind, promo.value)}
                            <span className="text-sm text-muted-foreground ml-2">OFF</span>
                          </p>
                        </div>

                        {/* Dates */}
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Starts: {new Date(promo.starts_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Ends: {new Date(promo.ends_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
